import * as React from 'react';
import { Table } from 'reactstrap';
import { LOCAL_DATE_FORMAT } from '../../../constants';
import { Choice } from '../../../widgets/ChoiceSelector';
import LocalDate from '../../../widgets/LocalDate';
import ScaledBytes from '../../../widgets/ScaledBytes';
import ScaledCount from '../../../widgets/ScaledCount';
import SortableTableColumnHeader from '../../../widgets/SortableTableColumnHeader';
import Sparkline from '../../../widgets/Sparkline';
import { SetFilterValuesFunction } from '../../FilterManager';
import { COMMON_DATA_FILTERS } from './data_constants';
import PivotButton, { AdditionalPivots } from './PivotButton';
import { BaseDataEntry } from './BaseDataView';

export enum DataColumnTypes {
  COUNT = 'count',
  BYTES = 'bytes',
  TEXT = 'text',
  SPARKLINE = 'sparkline',
}

type CountColumn = {
  type: 'count';
  field: string;
  title: string;
  sortable: boolean;
};

type BytesColumn = {
  type: 'bytes';
  field: string;
  title: string;
  sortable: boolean;
};

type TextColumn = {
  type: 'text';
  field: string;
  title: string;
  sortable: boolean;
};

type SparklineColumn = {
  type: 'sparkline';
  field: string;
  title: string;
  suffix: string;
};

export type DataColumn =
  | CountColumn
  | BytesColumn
  | TextColumn
  | SparklineColumn;

export type Props<D extends BaseDataEntry, V extends string> = {
  columns: DataColumn[];
  data: Array<D>;
  filters: URLSearchParams;
  onViewChange: (arg0: string) => void;
  setFilterValues: SetFilterValuesFunction;
  type: V;
  viewChoices: Choice[];
  defaultSort: string;
  additionalPivotOptions?: Array<AdditionalPivots>;
};

type State = {
  pivotPopupIndex: number | null;
  disabledPivots: Array<string>;
  sort: string;
};

export default class DataResultTable<
  D extends BaseDataEntry,
  V extends string,
> extends React.Component<Props<D, V>, State> {
  constructor(props: Props<D, V>) {
    super(props);
    this.state = {
      pivotPopupIndex: null,
      ...this.getSortAndPivotFromProps(props),
    };
  }

  componentDidUpdate(PrevProps: Props<D, V>) {
    // when the current view or filters change, update what pivots are available
    if (
      this.props.filters.toString() !== PrevProps.filters.toString() ||
      this.props.type !== PrevProps.type
    ) {
      this.setState(this.getSortAndPivotFromProps(this.props));
    }
  }

  /**
   * Returns the sort order and disabled pivots in an object matching state from the supplied props
   * @param {Props} props - the props object to determine sort and unavailable pivots from
   * @return {{disabledPivots: string[], sort: string}}
   */
  getSortAndPivotFromProps = (props: Props<D, V>) => {
    const filtersAndCurrentView: Array<string> = Array.from(
      props.filters.keys(),
    ).concat([props.type]);
    const disabledPivots: Array<string> = this.props.viewChoices
      .map((view: Choice) => view.value)
      .filter((viewType) => filtersAndCurrentView.includes(viewType));
    const sort = props.filters.get('sort') || this.props.defaultSort;
    return { disabledPivots, sort };
  };

  /**
   * Toggles the sorting of the table by setting it to the specified column, or inverting
   * the sort direction if it is already that column.
   * @param column {string} - name of the sort token
   */
  toggleSort = (column: string): void => {
    const { sort } = this.state;
    const pair = { key: 'sort', value: '-' + column };
    if (sort === '-' + column) {
      pair.value = column;
    } else if ('-' + column === this.props.defaultSort) {
      // special case to 'clear' sort for the default
      pair.value = '';
    }
    this.props.setFilterValues([pair]);
  };

  /**
   * Handler for opening and closing of the pivot/drill-down popup.
   * @param {number} index - the row index to toggle, or null to close all popups
   */
  togglePivotPopup = (index: number | null) => {
    this.setState((prevState) => ({
      pivotPopupIndex: index === prevState.pivotPopupIndex ? null : index,
    }));
  };

  /**
   * Event handler function responsible for applying the requested pivot filter action.
   * Using the supplied index, the 'name' attribute (first value) in the row will be set as
   * the filter value for the current view type, and then the view switched to that specified
   * as the second argument.
   * @param {number} index - the row index in the dataset to get the desired filter value
   * @param {string} view - the new view to pivot to
   */
  doPivot = (index: number, view: string) => {
    const { type, data, setFilterValues, onViewChange } = this.props;
    setFilterValues([
      { key: type, value: data[index].code || data[index].name },
    ]);
    onViewChange(view);
  };

  render() {
    const {
      columns,
      data,
      filters,
      type,
      viewChoices,
      additionalPivotOptions,
      setFilterValues,
    } = this.props;
    const { disabledPivots, sort, pivotPopupIndex } = this.state;
    const rows = (data || []).slice(0, 100); // Show up to 100, 101 indicates more than 100
    const canPivot = disabledPivots.length < viewChoices.length;

    return (
      <Table id="data-table" className="table-responsive-lg">
        <thead>
          <tr>
            <SortableTableColumnHeader
              name="name"
              sort={sort}
              toggleSort={this.toggleSort}
            >
              <span style={{ textTransform: 'capitalize' }}>
                {type.replace('_', ' ')}
              </span>
            </SortableTableColumnHeader>

            {columns.map((col, index) => {
              if (!rows[0].hasOwnProperty(col.field)) return null;
              switch (col.type) {
                case 'sparkline':
                  return (
                    <SortableTableColumnHeader key={index} name={col.field}>
                      <div
                        className={
                          'd-flex justify-content-between align-items-baseline'
                        }
                      >
                        <LocalDate
                          date={filters.get(COMMON_DATA_FILTERS.START) || ''}
                          localFormat={LOCAL_DATE_FORMAT}
                        />
                        {col.title}
                        <LocalDate
                          date={filters.get(COMMON_DATA_FILTERS.END) || ''}
                          localFormat={LOCAL_DATE_FORMAT}
                        />
                      </div>
                    </SortableTableColumnHeader>
                  );
                case 'text':
                case 'bytes':
                case 'count':
                  if (col.sortable) {
                    return (
                      <SortableTableColumnHeader
                        key={index}
                        name={col.field}
                        sort={sort}
                        toggleSort={this.toggleSort}
                      >
                        {col.title}
                      </SortableTableColumnHeader>
                    );
                  } else {
                    return (
                      <SortableTableColumnHeader key={index} name={col.field}>
                        {col.title}
                      </SortableTableColumnHeader>
                    );
                  }
                default:
                  return undefined;
              }
            })}

            {canPivot && (
              <SortableTableColumnHeader name="drill" className="pivot-column">
                Pivot
              </SortableTableColumnHeader>
            )}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr key={index}>
              <td>{row.name}</td>

              {columns.map((col, index) => {
                if (!row.hasOwnProperty(col.field)) return null;
                switch (col.type) {
                  case 'sparkline':
                    return (
                      <td key={index} style={{ width: '50%' }}>
                        <Sparkline
                          data={row[col.field]}
                          tooltipValueSuffix={col.suffix || ''}
                        />
                      </td>
                    );
                  case 'count':
                    return (
                      <td key={index} className="text-right">
                        <ScaledCount count={row[col.field] || 0} />
                      </td>
                    );
                  case 'bytes':
                    return (
                      <td key={index} className="text-right">
                        <ScaledBytes bytes={row[col.field] || 0} decimals={1} />
                      </td>
                    );
                  case 'text':
                    return <td key={index}>{row[col.field]}</td>;
                  default:
                    return undefined;
                }
              })}

              {canPivot && (
                <td className="pivot-column">
                  <PivotButton
                    viewChoices={viewChoices}
                    filters={filters}
                    disabledPivots={disabledPivots}
                    additionalPivots={additionalPivotOptions}
                    index={index}
                    isOpen={pivotPopupIndex === index}
                    onPivot={this.doPivot}
                    setFilterValues={setFilterValues}
                    togglePopup={this.togglePivotPopup}
                  />
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </Table>
    );
  }
}
